import React from 'react'
import { Box, Typography } from '@mui/material'

import SectionCollapse from '../../../client/layout/SectionCollapse'
import {
  UI_COLOR_BORDER_MUTED,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_SHADOW_CARD,
  UI_COLOR_TEXT_BODY_DARK,
  UI_COLOR_TEXT_SUMMARY_LABEL,
  UI_COLOR_WHITE
} from '../../../constants/uiColors'
import {
  UI_TEXT_CLOSE_DETAILS,
  UI_TEXT_ADDITIONAL_DATA,
  UI_TEXT_DETAILS,
  UI_TEXT_EMPTY_VALUE,
  UI_TEXT_NO,
  UI_TEXT_OPEN_ADDITIONAL_DATA,
  UI_TEXT_CLOSE_ADDITIONAL_DATA,
  UI_TEXT_OPEN_DETAILS,
  UI_TEXT_YES
} from '../../../constants/uiText'
import GoogleIcon from '../../icon/GoogleIcon'

const DETAIL_SECTION_CARD_BASE_SX = {
  border: 'none !important',
  borderRadius: '10px !important',
  mb: 1.5,
  backgroundColor: `${UI_COLOR_WHITE} !important`,
  boxShadow: `${UI_COLOR_SHADOW_CARD} !important`
}

const buildGridColumns = (columns) => {
  if (typeof columns === 'object') return columns
  if (typeof columns === 'number' && columns > 0)
    return { xs: '1fr', md: `repeat(${columns}, 1fr)` }
  return { xs: '1fr', md: '1fr 1fr' }
}

const UI_TEXT_SCOPE = 'CLIENTES Y ATRIBUTOS'
const UI_TEXT_ASSIGNMENT = 'TAREA ASIGNADA A'

function formatDetailValue(value) {
  if (typeof value === 'boolean') return value ? UI_TEXT_YES : UI_TEXT_NO
  if (value == null || value === '') return UI_TEXT_EMPTY_VALUE
  return value
}

function DetailRow({
  label,
  value,
  secondaryValue = null,
  fullWidth = false,
  hideLabel = false,
  inlineLabel = false
}) {
  const labelNode = (
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 800,
        color: UI_COLOR_TEXT_SUMMARY_LABEL,
        whiteSpace: inlineLabel ? 'nowrap' : 'normal'
      }}
    >
      {label}:
    </Typography>
  )
  const valueNode = (
    <Typography
      component="div"
      sx={{ fontSize: 14, color: UI_COLOR_TEXT_BODY_DARK, wordBreak: 'break-word' }}
    >
      {formatDetailValue(value)}
    </Typography>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minHeight: '100%',
        py: inlineLabel ? 2 : 1.1,
        px: 1.5,
        border: `1px solid ${UI_COLOR_BORDER_MUTED}`,
        borderRadius: 2,
        backgroundColor: UI_COLOR_WHITE,
        ...(fullWidth ? { gridColumn: '1 / -1' } : {})
      }}
    >
      {inlineLabel && !hideLabel ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.75,
              flexWrap: 'wrap',
              '& > .MuiTypography-root:last-child': {
                flex: 1,
                minWidth: 0
              }
            }}
          >
            {labelNode}
            {valueNode}
          </Box>
          {secondaryValue}
        </>
      ) : (
        <>
          {!hideLabel && labelNode}
          {valueNode}
          {secondaryValue}
        </>
      )}
    </Box>
  )
}

function SectionFields({ fields, columns, summarySingleItemFullWidth, extraFieldsCount = 0 }) {
  const visibleFields = fields.filter((field) => !field.hidden)

  if (visibleFields.length === 0) return null

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: buildGridColumns(columns),
        gap: 1.25
      }}
    >
      {visibleFields.map((field, index) => (
        <DetailRow
          key={`${field.label}-${index}`}
          label={field.label}
          value={field.value}
          secondaryValue={field.secondaryValue}
          fullWidth={
            field.fullWidth ||
            (summarySingleItemFullWidth && visibleFields.length === 1 && extraFieldsCount === 0)
          }
          hideLabel={field.hideLabel}
          inlineLabel={field.inlineLabel}
        />
      ))}
    </Box>
  )
}

function DetailSection({
  title,
  type,
  defaultOpen,
  openLabel,
  closeLabel,
  icon,
  headerSx,
  toggleSx,
  sectionSx,
  children
}) {
  return (
    <SectionCollapse
      title={title}
      type={type}
      defaultOpen={defaultOpen}
      ariaLabelOpen={openLabel}
      ariaLabelClose={closeLabel}
      icon={icon}
      sx={{ ...DETAIL_SECTION_CARD_BASE_SX, ...sectionSx }}
      headerSx={headerSx}
      toggleSx={toggleSx}
    >
      {children}
    </SectionCollapse>
  )
}

/**
 * Cómo reutilizar este componente:
 *
 * <DetailsSummarySection
 *   summaryFields={[
 *     { label: 'Cliente', value: clientName, fullWidth: true },
 *     { label: 'Código', value: clientCode }
 *   ]}
 *   extraFields={[
 *     { label: 'Comentario', value: comment }
 *   ]}
 *   summaryTitle="DETALLES DE DEVOLUCIÓN"
 * />
 *
 * Props:
 * - summaryFields: campos principales del detalle.
 * - extraFields: campos adicionales (custom); se muestran en la misma card.
 * - label: título del campo.
 * - value: valor que se va a pintar; vacío se muestra "-", 0 se conserva y los booleanos van como Sí/No.
 * - fullWidth: hace que ese campo ocupe todo el ancho de la grilla.
 * - summarySingleItemFullWidth: si hay un solo summaryField y no hay extraFields, lo expande a todo el ancho.
 * - summaryColumns: define columnas responsive de la grilla.
 * - summaryTitle: título visible de la sección.
 * - summaryOpenLabel / summaryCloseLabel: textos accesibles del botón abrir/cerrar.
 */
export default function DetailsSummarySection({
  summaryFields = [],
  scopeFields = [],
  assignmentFields = [],
  assignmentEmptyContent = null,
  extraFields = [],
  extraContent = null,
  summarySingleItemFullWidth = false,
  summaryTitle = UI_TEXT_DETAILS,
  scopeTitle = UI_TEXT_SCOPE,
  assignmentTitle = UI_TEXT_ASSIGNMENT,
  extraTitle = UI_TEXT_ADDITIONAL_DATA,
  summaryOpenLabel = UI_TEXT_OPEN_DETAILS,
  summaryCloseLabel = UI_TEXT_CLOSE_DETAILS,
  scopeOpenLabel = 'Abrir clientes y atributos',
  scopeCloseLabel = 'Cerrar clientes y atributos',
  assignmentOpenLabel = 'Abrir tarea asignada a',
  assignmentCloseLabel = 'Cerrar tarea asignada a',
  extraOpenLabel = UI_TEXT_OPEN_ADDITIONAL_DATA,
  extraCloseLabel = UI_TEXT_CLOSE_ADDITIONAL_DATA,
  summaryIcon = null,
  scopeIcon = null,
  assignmentIcon = null,
  extraIcon = null,
  summaryColumns = { xs: '1fr', md: '1fr 1fr' },
  scopeColumns = { xs: '1fr', md: '1fr 1fr' },
  assignmentColumns = { xs: '1fr' },
  extraColumns = { xs: '1fr' },
  secondarySectionsType = 'secondary',
  sectionHeaderSx = {},
  sectionToggleSx = {},
  sectionCardSx = {},
  summaryDefaultOpen = true
}) {
  const resolvedSummaryIcon = summaryIcon || (
    <GoogleIcon name="fact_check" size={20} color={UI_COLOR_PRIMARY_ALT} />
  )
  const resolvedScopeIcon = scopeIcon || (
    <GoogleIcon name="hub" size={20} color={UI_COLOR_PRIMARY_ALT} />
  )
  const resolvedAssignmentIcon = assignmentIcon || (
    <GoogleIcon name="groups" size={20} color={UI_COLOR_PRIMARY_ALT} />
  )
  const resolvedExtraIcon = extraIcon || (
    <GoogleIcon name="list_alt" size={20} color={UI_COLOR_PRIMARY_ALT} />
  )
  const visibleSummaryFields = summaryFields.filter((field) => !field.hidden)
  const visibleScopeFields = scopeFields.filter((field) => !field.hidden)
  const visibleAssignmentFields = assignmentFields.filter((field) => !field.hidden)
  const visibleExtraFields = extraFields.filter((field) => !field.hidden)
  const hasFields =
    visibleSummaryFields.length > 0 ||
    visibleScopeFields.length > 0 ||
    visibleAssignmentFields.length > 0 ||
    visibleExtraFields.length > 0 ||
    Boolean(assignmentEmptyContent) ||
    Boolean(extraContent)

  if (!hasFields) return null

  return (
    <>
      {visibleSummaryFields.length > 0 && (
        <DetailSection
          title={summaryTitle}
          type="title"
          defaultOpen={summaryDefaultOpen}
          openLabel={summaryOpenLabel}
          closeLabel={summaryCloseLabel}
          icon={resolvedSummaryIcon}
          headerSx={sectionHeaderSx}
          toggleSx={sectionToggleSx}
          sectionSx={sectionCardSx}
        >
          <SectionFields
            fields={visibleSummaryFields}
            columns={summaryColumns}
            summarySingleItemFullWidth={summarySingleItemFullWidth}
            extraFieldsCount={visibleExtraFields.length}
          />
        </DetailSection>
      )}

      {visibleScopeFields.length > 0 && (
        <DetailSection
          title={scopeTitle}
          type={secondarySectionsType}
          defaultOpen
          openLabel={scopeOpenLabel}
          closeLabel={scopeCloseLabel}
          icon={resolvedScopeIcon}
          headerSx={sectionHeaderSx}
          toggleSx={sectionToggleSx}
          sectionSx={sectionCardSx}
        >
          <SectionFields fields={visibleScopeFields} columns={scopeColumns} />
        </DetailSection>
      )}

      {(visibleAssignmentFields.length > 0 || assignmentEmptyContent) && (
        <DetailSection
          title={assignmentTitle}
          type={secondarySectionsType}
          defaultOpen
          openLabel={assignmentOpenLabel}
          closeLabel={assignmentCloseLabel}
          icon={resolvedAssignmentIcon}
          headerSx={sectionHeaderSx}
          toggleSx={sectionToggleSx}
          sectionSx={sectionCardSx}
        >
          {visibleAssignmentFields.length > 0 ? (
            <SectionFields fields={visibleAssignmentFields} columns={assignmentColumns} />
          ) : (
            assignmentEmptyContent
          )}
        </DetailSection>
      )}

      {(visibleExtraFields.length > 0 || extraContent) && (
        <DetailSection
          title={extraTitle}
          type={secondarySectionsType}
          defaultOpen
          openLabel={extraOpenLabel}
          closeLabel={extraCloseLabel}
          icon={resolvedExtraIcon}
          headerSx={sectionHeaderSx}
          toggleSx={sectionToggleSx}
          sectionSx={sectionCardSx}
        >
          <SectionFields fields={visibleExtraFields} columns={extraColumns} />
          {extraContent}
        </DetailSection>
      )}
    </>
  )
}
